import { GraphqlApi, IGraphqlApi } from "@/framework/network/gql/GraphqlApi";
import {
  QueryLocationGroup,
  QueryLocationGroupSchema,
  QueryLocationGroupWithoutRelations,
  QueryLocationGroupWithoutRelationsSchema,
  QueryStaffGroupState,
  QueryStaffGroupStateSchema,
  QueryTenant,
  QueryTenantSchema,
  SubMenuInUse,
  UpdateAdminTenantParam,
} from "@/modules/admin/objects/hotelAdmin";
import { i18n } from "@/framework/plugins/i18n";
import store from "@/store";
import { Mutation as HotelMutation } from "@/framework/store/hotel/types";
import {
  GetBigQueryDashboardUrlDocument,
  ListStaffGroupWithStateDocument,
  LocationGroupChatHistorySettingInput,
  SwitchLiveChatWidgetDocument,
  UpdateBizLocationGroupChatHistoriesDocument,
  UpdateBizTenantInfoDocument,
  UpdateCustomizedSubMenuDocument,
  ChangeBizLocationGroupColorDocument,
  CreateBizLocationGroupDocument,
  DeleteBizLocationGroupDocument,
  UpdateBizLocationGroupDocument,
  GetLocationsDocument,
  GetBigQueryDashboardUrlQuery,
  GetLocationsQuery,
  UpdateBizTenantInfoMutation,
  UpdateBizLocationGroupChatHistoriesMutation,
  UpdateCustomizedSubMenuMutation,
  ListStaffGroupWithStateQuery,
  SwitchLiveChatWidgetMutation,
  CreateBizLocationGroupMutation,
  DeleteBizLocationGroupMutation,
  UpdateBizLocationGroupMutation,
  ChangeBizLocationGroupColorMutation,
} from "@/framework/network/gql/generated/graphql";
import { GeneralResult, GeneralResultSchema } from "@/framework/objects/GeneralResult";
import { err, ok, Result } from "neverthrow";
import { ZodError } from "zod";
import GraphqlApiError from "@/framework/network/gql/GraphqlApiError";
import { CustomTenantInfoAdminLocationGroups } from "@/framework/network/gql/custom/GetBizTenantInfoAdmin/CustomTenantInfoAdminLocationGroups";
import { CustomTenantInfo } from "@/framework/network/gql/custom/GetBizTenantInfo/CustomTenantInfo";
import { CustomTenantInfoMenus } from "@/framework/network/gql/custom/GetBizTenantInfo/CustomTenantInfoMenus";
import { GeneralResponse, GeneralResponseSchema } from "@/framework/objects/GeneralResponse";
import { QcBizLocation, QcBizLocationSchema } from "@/modules/staff/objects/QcBizLocation";
import { z } from "zod";

export type BigQueryDashboardUrlResponse = z.infer<typeof BigQueryDashboardUrlResponseSchema>;
export const BigQueryDashboardUrlResponseSchema = z.object({
  isDisplay: z.boolean().optional(),
  url: z.string().optional(),
});

export class TenantService {
  private _graphql: IGraphqlApi;

  public constructor(graphql: IGraphqlApi = new GraphqlApi()) {
    this._graphql = graphql;
  }

  async getDashboardUrl(tenantId: number): Promise<Result<BigQueryDashboardUrlResponse, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<GetBigQueryDashboardUrlQuery, BigQueryDashboardUrlResponse>(
      // @ts-ignore conflict between generic typing and exact requirement
      GetBigQueryDashboardUrlDocument,
      BigQueryDashboardUrlResponseSchema,
      { tenantId }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async fetchLocation(groupId: number): Promise<Result<QcBizLocation[], GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<GetLocationsQuery, QcBizLocation[]>(
      GetLocationsDocument,
      QcBizLocationSchema,
      { locationGroupId: groupId }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async getLocationGroups(noCache: boolean): Promise<Result<QueryTenant, GraphqlApiError | ZodError>> {
    const res = await this._graphql.runQuery(CustomTenantInfoAdminLocationGroups, { langCd: i18n.locale }, noCache);
    return res.isErr() ? err(res.error) : ok(res.value.getBizTenantInfoAdmin);
  }

  async fetchTenantInfo(withMenus = false, noCache = false): Promise<Result<QueryTenant, GraphqlApiError | ZodError>> {
    const res = await this._graphql.runQuery(
      withMenus ? CustomTenantInfoMenus : CustomTenantInfo,
      { langCd: i18n.locale },
      noCache
    );

    if (res.isOk()) {
      const result = res.value.getBizTenantInfo;
      store.commit(HotelMutation.SET_TENANT_ID, result.id);
      store.commit(HotelMutation.SET_TENANT_LANG_CD, result.lang_cd);
      if (!withMenus) store.commit(HotelMutation.SET_TENANT_INFO, result);
      return ok(result);
    } else {
      return err(res.error);
    }
  }

  async updateTenantInfo(param: UpdateAdminTenantParam): Promise<Result<GeneralResponse, GraphqlApiError | ZodError>> {
    const res = await this._graphql.runMutationAndValidate<UpdateBizTenantInfoMutation, GeneralResponse>(
      UpdateBizTenantInfoDocument,
      GeneralResponseSchema,
      param
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async updateGroupChatHistories(
    input: LocationGroupChatHistorySettingInput[]
  ): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<
      UpdateBizLocationGroupChatHistoriesMutation,
      GeneralResponse
    >(
      // @ts-ignore conflict between generic typing and exact requirement
      UpdateBizLocationGroupChatHistoriesDocument,
      GeneralResponseSchema,
      { chat_history_settings: input }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async updateCustomizedSubMenuName(
    subMenu: SubMenuInUse,
    langCd: string
  ): Promise<Result<GeneralResult, GraphqlApiError | ZodError>> {
    const res = await this._graphql.runMutationAndValidate<UpdateCustomizedSubMenuMutation, GeneralResult>(
      // @ts-ignore conflict between generic typing and exact requirement
      UpdateCustomizedSubMenuDocument,
      GeneralResultSchema,
      { langCd: langCd, message: subMenu.name, subMenuId: subMenu.id }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async fetchStaffGroupsWithState(): Promise<Result<QueryStaffGroupState[], GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<ListStaffGroupWithStateQuery, QueryStaffGroupState[]>(
      // @ts-ignore conflict between generic typing and exact requirement
      ListStaffGroupWithStateDocument,
      QueryStaffGroupStateSchema,
      {}
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async toggleLiveChatWidget(widgetOnOff: boolean): Promise<Result<GeneralResult, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<SwitchLiveChatWidgetMutation, GeneralResult>(
      SwitchLiveChatWidgetDocument,
      GeneralResultSchema,
      { widget_on_off: widgetOnOff }
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  //////////////////////////////////////////////////////////////////
  //  APIs for menu groups  ////////////////////////////////////////
  //////////////////////////////////////////////////////////////////

  // QcBizLocationGroupOnly
  async createBizLocationGroup(groupName: string): Promise<Result<QueryLocationGroup, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<CreateBizLocationGroupMutation, QueryLocationGroup>(
      // @ts-ignore conflict between generic typing and exact requirement
      CreateBizLocationGroupDocument,
      QueryLocationGroupSchema,
      { display_name: groupName }
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async deleteBizLocationGroup(groupId: number): Promise<Result<GeneralResult, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<DeleteBizLocationGroupMutation, GeneralResult>(
      // @ts-ignore conflict between generic typing and exact requirement
      DeleteBizLocationGroupDocument,
      GeneralResultSchema,
      { location_group_id: groupId }
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async updateBizLocationGroup(
    groupId: number,
    newName: string
  ): Promise<Result<QueryLocationGroupWithoutRelations, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<
      UpdateBizLocationGroupMutation,
      QueryLocationGroupWithoutRelations
    >(
      // @ts-ignore conflict between generic typing and exact requirement
      UpdateBizLocationGroupDocument,
      QueryLocationGroupWithoutRelationsSchema,
      { location_group_id: groupId, display_name: newName }
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  // including because it was created, but according to PO, users can't change this value
  async changeBizLocationGroupColor(groupId: number): Promise<Result<QueryLocationGroup, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<ChangeBizLocationGroupColorMutation, QueryLocationGroup>(
      // @ts-ignore conflict between generic typing and exact requirement
      ChangeBizLocationGroupColorDocument,
      QueryLocationGroupSchema,
      { location_group_id: groupId }
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }
}
